import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Upload, Button, Select, Input, Space, Popconfirm, message, Alert } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getCertificateTemplates, uploadCertificateTemplate, deleteCertificateTemplate,
} from '../../api/preschools';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Sertifika şablon havuzu — KURUM GENELİ, okula bağlı değil (kullanıcı
 * kararı): aynı tasarımı kullanan 20 okul için dosya bir kez yükleniyor.
 *
 * Şablon `.docx` olmalı ve içinde yer tutucular bulunmalı. Motor ARYS'nin
 * sertifika motoruyla AYNI — orada çözülmüş Türkçe büyük harf, arka plan
 * görseli ve yatay/dikey sayfa tespiti burada da geçerli.
 */
const YER_TUTUCULAR = ['{ad_soyad}', '{sertifika_no}', '{tarih}', '{okul_adi}', '{sinif_adi}'];

const PreschoolCertificateTemplates = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [termNo, setTermNo] = useState(1);
  const [ad, setAd] = useState('');
  const [dosya, setDosya] = useState(null);
  const [saving, setSaving] = useState(false);

  const yukle = useCallback(() => {
    setLoading(true);
    getCertificateTemplates()
      .then(({ data }) => setRows(data || []))
      .catch(() => message.error('Şablonlar yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) yukle(); }, [open, yukle]);

  const handleUpload = async () => {
    if (!dosya) { message.warning('Dosya seçiniz.'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('term_no', termNo);
      fd.append('file', dosya);
      if (ad) fd.append('name', ad);

      await uploadCertificateTemplate(fd);
      message.success('Şablon yüklendi.');
      setDosya(null);
      setAd('');
      yukle();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Şablon yüklenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Sertifika Şablonları" open={open} onCancel={onClose} footer={null} width={720}>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Şablon .docx olmalı"
        description={(
          <div className="text-sm">
            Word dosyasının içine şu yer tutucuları yazın, üretim sırasında
            doldurulur: <code>{YER_TUTUCULAR.join('  ')}</code>
            <div className="mt-2 text-gray-500">
              Hepsi aynı tasarımsa tek şablon yüklemeniz yeterli — dönem için
              ayrı şablon yoksa havuzdaki tek aktif şablon kullanılır.
            </div>
          </div>
        )}
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'Henüz şablon yüklenmemiş.' }}
        columns={[
          { title: 'Dönem', dataIndex: 'term_no', width: 90, render: (v) => `${v}. Dönem` },
          { title: 'Ad', dataIndex: 'name', render: (v) => v || '—' },
          {
            title: '',
            key: 'sil',
            width: 50,
            render: (_, r) => (
              <Popconfirm
                title="Şablon silinsin mi?"
                description="Üretilmiş sertifikalar etkilenmez."
                onConfirm={async () => {
                  try {
                    await deleteCertificateTemplate(r.id);
                    message.success('Şablon silindi.');
                    yukle();
                  } catch (error) {
                    message.error(apiErrorMessage(error, 'Silinemedi.'));
                  }
                }}
                okText="Sil"
                cancelText="Vazgeç"
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
      />

      <Space className="mt-4" wrap>
        <Select
          value={termNo}
          onChange={setTermNo}
          style={{ width: 130 }}
          options={[1, 2, 3, 4].map((n) => ({ value: n, label: `${n}. Dönem` }))}
        />
        <Input
          placeholder="Şablon adı (isteğe bağlı)"
          value={ad}
          onChange={(e) => setAd(e.target.value)}
          style={{ width: 220 }}
        />
        <Upload
          accept=".docx"
          maxCount={1}
          beforeUpload={(f) => { setDosya(f); return false; }}
          onRemove={() => setDosya(null)}
          fileList={dosya ? [dosya] : []}
        >
          <Button icon={<UploadOutlined />}>Dosya Seç</Button>
        </Upload>
        <Button type="primary" onClick={handleUpload} loading={saving} disabled={!dosya}>
          Yükle
        </Button>
      </Space>
      <div className="text-xs text-gray-400 mt-2">
        Aynı döneme yeni dosya yüklerseniz öncekinin yerine geçer.
      </div>
    </Modal>
  );
};

export default PreschoolCertificateTemplates;
